import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';

import IconButton from '@material-ui/core/IconButton';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

import type OrderItem from '@this/src/domain/order_item2';
import type TransportElement from '@this/src/domain/transport_element2';

interface Props {
  orderItem: OrderItem;
  element: TransportElement;
  elementIndex: number;
}

const getIsShinkansen = (element: TransportElement) => {
  return element.isShinkansen || element.isExpress || element.isForeignRailway;
};

const getIsExic = (element: TransportElement, orderItem: OrderItem, elementIndex: number) => {
  if (element.railwayTicketType === 3) return 'all';
  if (orderItem.travelerInformations.length < 1) return false;

  const travelerSome = orderItem.travelerInformations.some((traveler: any) => {
    if (
      traveler.travelerInformationShinkansenTypes &&
      traveler.travelerInformationShinkansenTypes.length > 0 &&
      traveler.travelerInformationShinkansenTypes[elementIndex]
    ) {
      return traveler.travelerInformationShinkansenTypes[elementIndex].shinkansenTicketType === 'ex';
    }
    return traveler.shinkansenTicketType === 'ex';
  });
  if (travelerSome) return 'part';

  return false;
};

const getTravelers = (orderItem: OrderItem, isExic: 'all' | 'part' | false, elementIndex: number) => {
  if (isExic === 'all') return orderItem.travelerInformations;
  if (isExic === 'part') {
    return orderItem.travelerInformations.filter((traveler: any) => {
      if (traveler.travelerInformationShinkansenTypes && traveler.travelerInformationShinkansenTypes.length > 0) {
        return traveler.travelerInformationShinkansenTypes[elementIndex]?.shinkansenTicketType === 'ex';
      }
      return traveler.shinkansenTicketType === 'ex';
    });
  }
  return [];
};

export const UserExicCard: React.FC<Props> = observer(({ orderItem, element, elementIndex }) => {
  const [openIds, setOpenIds] = React.useState<number[]>([]);
  const isShinkansen = useMemo(() => getIsShinkansen(element), [element]);
  const isExic = useMemo(
    () => getIsExic(element, orderItem, elementIndex),
    [element, orderItem.travelerInformations, elementIndex]
  );
  const travelers = useMemo(
    () => getTravelers(orderItem, isExic, elementIndex),
    [isExic, orderItem.travelerInformations, elementIndex]
  );
  const displayTravelers = useMemo(
    () =>
      travelers.filter((ti: any) => {
        const usageMode = ti?.user?.organization?.exicUsageMode;
        if (usageMode === 'card_based') return !!ti?.user?.exicCard;
        return true;
      }),
    [travelers]
  );

  const handleToggleOpen = useCallback(
    (id: number) => {
      setOpenIds(openIds => {
        if (openIds.includes(id)) {
          return openIds.filter(openId => openId !== id);
        }
        return [...openIds, id];
      });
    },
    [setOpenIds]
  );

  const renderExicInfo = (ti: any) => {
    const usageMode = ti?.user?.organization?.exicUsageMode;

    if (usageMode === 'card_based') {
      const exicNumber = ti?.user?.exicCard?.exicNumber;
      const exicPassword = ti?.user?.exicCard?.password;
      return (
        <>
          <p>EXIC会員ID：{exicNumber || '未設定'}</p>
          {renderPassword(exicPassword, ti.id)}
        </>
      );
    }

    if (usageMode === 'user_based' || !ti?.user) {
      const exicId = ti?.user?.exicId;
      const exicPassword = ti?.user?.exicPassword;
      return (
        <>
          <p>EXIC会員ID：{exicId || '未設定'}</p>
          {renderPassword(exicPassword, ti.id)}
        </>
      );
    }
    return null;
  };

  const renderPassword = (password: string | undefined, id: number) => {
    if (password) {
      return (
        <p>
          EXICパスワード：
          {openIds.includes(id) ? password : password.replace(/./g, '*')}
          <IconButton size="small" onClick={() => handleToggleOpen(id)}>
            {openIds.includes(id) ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </p>
      );
    }
    return <p>EXICパスワード：未設定</p>;
  };

  if (!isShinkansen || !isExic || displayTravelers.length < 1) return null;

  return (
    <div data-testid="latest-exic-item-display">
      ■ EXIC（最新の情報）
      <br />
      {displayTravelers.map((ti: any) => (
        <div key={ti.id}>
          {ti.lastNameKana} {ti.firstNameKana}：
          <br />
          <div style={{ marginLeft: '10px' }}>{renderExicInfo(ti)}</div>
        </div>
      ))}
    </div>
  );
});
